import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Building2, ArrowLeft } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { countries, colombiaData, departments } from '../data/countries';
import { SECTORES_ECONOMICOS, SECTORES_LIST, calculateAge } from '../data/sectoresEconomicos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterJuridica({ onBack, darkMode = false }) {
  // Clases para inputs en modo oscuro
  const inputClass = `h-11 rounded-sm ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700 placeholder:text-gray-500' : ''}`;
  const selectTriggerClass = `h-11 rounded-sm ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700' : ''}`;
  const labelClass = `text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_type: 'juridica',
    // Datos de la empresa
    company_name: '',
    company_document_type: 'NIT',
    nit: '',
    company_verification_digit: '',
    // Representante Legal
    legal_rep_first_name: '',
    legal_rep_second_name: '',
    legal_rep_first_lastname: '',
    legal_rep_second_lastname: '',
    legal_rep_phone: '',
    legal_rep_email: '',
    legal_rep_address: '',
    legal_rep_neighborhood: '',
    legal_rep_birth_date: '',
    legal_rep_is_migrant: false,
    legal_rep_migrant_country: '',
    legal_rep_is_lgbtiq: '',
    legal_rep_is_caregiver: false,
    legal_rep_is_veteran: false,
    // Persona Asociada
    assoc_document_type: '',
    assoc_document_id: '',
    assoc_verification_digit: '',
    assoc_first_name: '',
    assoc_second_name: '',
    assoc_first_lastname: '',
    assoc_second_lastname: '',
    assoc_email: '',
    assoc_phone: '',
    assoc_biological_sex: '',
    assoc_role: '',
    assoc_position: '',
    assoc_is_migrant: false,
    assoc_migrant_country: '',
    assoc_is_lgbtiq: '',
    assoc_is_caregiver: false,
    assoc_is_veteran: false,
    assoc_residence_country: '',
    assoc_residence_department: '',
    assoc_residence_municipality: '',
    assoc_nationality: '',
    assoc_birth_date: '',
    assoc_is_youth_14_28: false,
    assoc_is_senior_60_plus: false,
    // Información Empresarial
    company_economic_activity: '',
    company_size: '',
    company_entrepreneurship_stage: '',
    company_ethnic_group: '',
    company_education_level: '',
    company_interest_area: '',
    company_vulnerable_population: false,
    company_vulnerable_population_type: '',
    company_victim_ruv: false,
    company_victim_ruv_number: '',
    company_has_disability: false,
    company_disability_type: '',
    company_in_minminas_census: false,
    // Ubicación geográfica de la empresa (rural / urbano)
    company_geographic_location: '',
    // Información del Beneficiario adicional
    company_beneficiary_neighborhood: '',
    company_beneficiary_address: '',
    company_beneficiary_phone: '',
    company_beneficiary_other_phone: '',
    company_beneficiary_email: '',
    // Producto y sector
    company_main_product: '',
    company_economic_sector: '',
    company_economic_subsector: '',
    // Formalización
    company_has_rut: false,
    company_has_mercantile_registry: false,
    company_mercantile_registry_date: '',
    // Datos empresariales
    company_employees_count: '',
    company_monthly_sales_intervention: '',
    company_total_assets_last_period: '',
    company_size_category: '',
    company_non_profit: false,
    company_has_website: false,
    company_website: '',
    // Áreas de interés (lista)
    company_interests: [],
    data_authorization: false,
    email: '',
    password: ''
  });

  // Calcular edad automáticamente para persona asociada
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const handleAssocBirthDateChange = (date) => {
    const age = calculateAge(date);
    if (age !== null) {
      setFormData(prev => ({
        ...prev,
        assoc_birth_date: date,
        assoc_is_youth_14_28: age >= 14 && age <= 28,
        assoc_is_senior_60_plus: age >= 60,
        company_assoc_age: age
      }));
    } else {
      setFormData(prev => ({ ...prev, assoc_birth_date: date, company_assoc_age: null }));
    }
  };

  // Calcular años de formalización desde fecha de matrícula mercantil
  const calculateFormalizationYears = (registryDate) => {
    if (!registryDate) return null;
    const today = new Date();
    const start = new Date(registryDate);
    let years = today.getFullYear() - start.getFullYear();
    const monthDiff = today.getMonth() - start.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < start.getDate())) {
      years -= 1;
    }
    return years >= 0 ? years : 0;
  };

  const handleMercantileRegistryDateChange = (date) => {
    const years = calculateFormalizationYears(date);
    setFormData(prev => ({
      ...prev,
      company_mercantile_registry_date: date,
      company_formalization_years: years
    }));
  };

  const toggleInterest = (option) => {
    setFormData(prev => {
      const current = prev.company_interests || [];
      const next = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return { ...prev, company_interests: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.data_authorization) {
      toast.error('Debes aceptar la autorización de tratamiento de datos personales');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('¡Registro exitoso! Revisa tu correo para verificar tu cuenta.');
      navigate('/verify', { state: { email: formData.email } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Botón Ir Atrás */}
      <button
        type="button"
        onClick={onBack}
        className={`flex items-center gap-2 text-sm font-medium mb-6 transition-colors ${
          darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
        }`}
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a selección de tipo
      </button>

      <form onSubmit={handleSubmit} className="space-y-6">

      {/* Datos de la Empresa */}
      <div className="space-y-4">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Datos de la Empresa</h3>
        
        <div className="space-y-2">
          <Label htmlFor="company_name" className={labelClass}>
            Razón Social *
          </Label>
          <Input
            id="company_name"
            type="text"
            placeholder="Empresa S.A.S."
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className={inputClass}
            required
            data-testid="register-company-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_document_type" className={labelClass}>
              Tipo de Identificación *
            </Label>
            <Select
              value={formData.company_document_type}
              onValueChange={(value) => setFormData({ ...formData, company_document_type: value })}
              required
            >
              <SelectTrigger className={selectTriggerClass} data-testid="register-company-document-type">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NIT">NIT</SelectItem>
                <SelectItem value="DOP">Documento de otro país</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="nit" className={labelClass}>
              NIT *
            </Label>
            <Input
              id="nit"
              type="text"
              placeholder="900123456"
              value={formData.nit}
              onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
              className={inputClass}
              required
              data-testid="register-nit-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_verification_digit" className={labelClass}>
              Dígito de Verificación
            </Label>
            <Input
              id="company_verification_digit"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              placeholder="0"
              value={formData.company_verification_digit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                setFormData({ ...formData, company_verification_digit: val });
              }}
              className={inputClass}
              data-testid="register-company-verification-digit-input"
            />
          </div>
        </div>
      </div>

      {/* Representante Legal */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Datos del Representante Legal</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="legal_rep_first_name" className={labelClass}>
              Primer Nombre *
            </Label>
            <Input
              id="legal_rep_first_name"
              type="text"
              placeholder="Juan"
              value={formData.legal_rep_first_name}
              onChange={(e) => setFormData({ ...formData, legal_rep_first_name: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="legal_rep_second_name" className={labelClass}>
              Segundo Nombre
            </Label>
            <Input
              id="legal_rep_second_name"
              type="text"
              placeholder="Carlos"
              value={formData.legal_rep_second_name}
              onChange={(e) => setFormData({ ...formData, legal_rep_second_name: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="legal_rep_first_lastname" className={labelClass}>
              Primer Apellido *
            </Label>
            <Input
              id="legal_rep_first_lastname"
              type="text"
              placeholder="Pérez"
              value={formData.legal_rep_first_lastname}
              onChange={(e) => setFormData({ ...formData, legal_rep_first_lastname: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="legal_rep_second_lastname" className={labelClass}>
              Segundo Apellido
            </Label>
            <Input
              id="legal_rep_second_lastname"
              type="text"
              placeholder="García"
              value={formData.legal_rep_second_lastname}
              onChange={(e) => setFormData({ ...formData, legal_rep_second_lastname: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="legal_rep_email" className={labelClass}>Email del Representante Legal</Label>
            <Input
              id="legal_rep_email"
              type="email"
              placeholder="rl@empresa.com"
              value={formData.legal_rep_email}
              onChange={(e) => setFormData({ ...formData, legal_rep_email: e.target.value })}
              className={inputClass}
              data-testid="legal-rep-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="legal_rep_phone" className={labelClass}>Teléfono del Representante Legal</Label>
            <Input
              id="legal_rep_phone"
              type="tel"
              placeholder="3001234567"
              maxLength={10}
              value={formData.legal_rep_phone}
              onChange={(e) => setFormData({ ...formData, legal_rep_phone: e.target.value })}
              className={inputClass}
              data-testid="legal-rep-phone-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="legal_rep_address" className={labelClass}>Dirección del Representante Legal</Label>
            <Input
              id="legal_rep_address"
              type="text"
              placeholder="Calle 123 # 45-67"
              value={formData.legal_rep_address}
              onChange={(e) => setFormData({ ...formData, legal_rep_address: e.target.value })}
              className={inputClass}
              data-testid="legal-rep-address-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="legal_rep_neighborhood" className={labelClass}>Barrio del Representante Legal</Label>
            <Input
              id="legal_rep_neighborhood"
              type="text"
              placeholder="Ej: Chapinero"
              value={formData.legal_rep_neighborhood}
              onChange={(e) => setFormData({ ...formData, legal_rep_neighborhood: e.target.value })}
              className={inputClass}
              data-testid="legal-rep-neighborhood-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="legal_rep_birth_date" className={labelClass}>Fecha de Nacimiento del Representante Legal</Label>
            <Input
              id="legal_rep_birth_date"
              type="date"
              value={formData.legal_rep_birth_date}
              onChange={(e) => setFormData({ ...formData, legal_rep_birth_date: e.target.value })}
              className={inputClass}
              data-testid="legal-rep-birthdate-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="legal_rep_age" className={labelClass}>Edad</Label>
            <Input
              id="legal_rep_age"
              type="text"
              value={calculateAge(formData.legal_rep_birth_date) !== '' ? `${calculateAge(formData.legal_rep_birth_date)} años` : ''}
              readOnly
              placeholder="Se calcula desde la fecha"
              className={`${inputClass} bg-slate-50`}
              data-testid="legal-rep-calculated-age"
            />
          </div>
        </div>
      </div>

      {/* Persona Asociada al Usuario */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Persona Asociada al Usuario</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_document_type" className={labelClass}>
              Tipo de Identificación *
            </Label>
            <Select
              value={formData.assoc_document_type}
              onValueChange={(value) => setFormData({ ...formData, assoc_document_type: value })}
              required
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CC">Cédula de Ciudadanía</SelectItem>
                <SelectItem value="CE">Cédula de Extranjería</SelectItem>
                <SelectItem value="TI">Tarjeta de Identidad</SelectItem>
                <SelectItem value="PAS">Pasaporte</SelectItem>
                <SelectItem value="PPT">Permiso por Protección Temporal</SelectItem>
                <SelectItem value="DOP">Documento de otro país</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_document_id" className={labelClass}>
              Número de Identificación *
            </Label>
            <Input
              id="assoc_document_id"
              type="text"
              placeholder="123456789"
              value={formData.assoc_document_id}
              onChange={(e) => setFormData({ ...formData, assoc_document_id: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_verification_digit" className={labelClass}>
              Dígito de Verificación
            </Label>
            <Input
              id="assoc_verification_digit"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              placeholder="0"
              value={formData.assoc_verification_digit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
                setFormData({ ...formData, assoc_verification_digit: val });
              }}
              className={inputClass}
              data-testid="register-juridica-verification-digit-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_first_name" className={labelClass}>
              Primer Nombre *
            </Label>
            <Input
              id="assoc_first_name"
              type="text"
              placeholder="María"
              value={formData.assoc_first_name}
              onChange={(e) => setFormData({ ...formData, assoc_first_name: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_second_name" className={labelClass}>
              Segundo Nombre
            </Label>
            <Input
              id="assoc_second_name"
              type="text"
              placeholder="Elena"
              value={formData.assoc_second_name}
              onChange={(e) => setFormData({ ...formData, assoc_second_name: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_first_lastname" className={labelClass}>
              Primer Apellido *
            </Label>
            <Input
              id="assoc_first_lastname"
              type="text"
              placeholder="González"
              value={formData.assoc_first_lastname}
              onChange={(e) => setFormData({ ...formData, assoc_first_lastname: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_second_lastname" className={labelClass}>
              Segundo Apellido
            </Label>
            <Input
              id="assoc_second_lastname"
              type="text"
              placeholder="López"
              value={formData.assoc_second_lastname}
              onChange={(e) => setFormData({ ...formData, assoc_second_lastname: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_email" className={labelClass}>
              Correo Electrónico *
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="assoc_email"
                type="email"
                placeholder="contacto@empresa.com"
                value={formData.assoc_email}
                onChange={(e) => setFormData({ ...formData, assoc_email: e.target.value })}
                className="pl-10 h-11 rounded-sm"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_phone" className={labelClass}>
              Teléfono Celular (10 dígitos) *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="assoc_phone"
                type="tel"
                placeholder="3001234567"
                value={formData.assoc_phone}
                onChange={(e) => setFormData({ ...formData, assoc_phone: e.target.value })}
                className="pl-10 h-11 rounded-sm"
                required
                maxLength={10}
                pattern="[0-9]{10}"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_biological_sex" className={labelClass}>
              Sexo Biológico *
            </Label>
            <Select
              value={formData.assoc_biological_sex}
              onValueChange={(value) => setFormData({ ...formData, assoc_biological_sex: value })}
              required
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hombre">Hombre</SelectItem>
                <SelectItem value="mujer">Mujer</SelectItem>
                <SelectItem value="intersexual">Intersexual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_birth_date" className={labelClass}>
              Fecha de Nacimiento *
            </Label>
            <Input
              id="assoc_birth_date"
              type="date"
              value={formData.assoc_birth_date}
              onChange={(e) => handleAssocBirthDateChange(e.target.value)}
              className={inputClass}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_age" className={labelClass}>Edad del Asociado</Label>
            <Input
              id="assoc_age"
              type="text"
              value={calculateAge(formData.assoc_birth_date) !== '' ? `${calculateAge(formData.assoc_birth_date)} años` : ''}
              readOnly
              placeholder="Se calcula desde la fecha"
              className={`${inputClass} bg-slate-50`}
              data-testid="assoc-calculated-age"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assoc_role" className={labelClass}>
              Rol de la Persona
            </Label>
            <Input
              id="assoc_role"
              type="text"
              placeholder="Emprendedor, Empresario, Aliado, etc."
              value={formData.assoc_role}
              onChange={(e) => setFormData({ ...formData, assoc_role: e.target.value })}
              className={`h-11 rounded-sm border border-slate-400 bg-white ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700 placeholder:text-gray-500' : ''}`}
              data-testid="register-juridica-assoc-role-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assoc_position" className={labelClass}>
              Cargo de la Persona
            </Label>
            <Input
              id="assoc_position"
              type="text"
              placeholder="Gerente General, Director, etc."
              value={formData.assoc_position}
              onChange={(e) => setFormData({ ...formData, assoc_position: e.target.value })}
              className={`h-11 rounded-sm border border-slate-400 bg-white ${darkMode ? 'bg-[#1a1a1a] text-white border-gray-700 placeholder:text-gray-500' : ''}`}
              data-testid="register-juridica-assoc-position-input"
            />
          </div>
        </div>

        {/* Características de la Persona Asociada */}
        <div className="space-y-4 mt-6">
          <h4 className="text-base font-semibold text-primary">Características de la Persona Asociada</h4>
          
          <div className="space-y-2">
            <Label className={labelClass}>¿Usted es una persona migrante?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.assoc_is_migrant === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_migrant === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_migrant: false, assoc_migrant_country: '' })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.assoc_is_migrant === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_migrant === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_migrant: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          {formData.assoc_is_migrant && (
            <div className="space-y-2">
              <Label htmlFor="assoc_migrant_country" className={labelClass}>
                País Migrante *
              </Label>
              <Select
                value={formData.assoc_migrant_country}
                onValueChange={(value) => setFormData({ ...formData, assoc_migrant_country: value })}
                required
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Seleccione país..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="assoc_is_lgbtiq" className={labelClass}>
              ¿Usted hace parte de los sectores sociales LGBTIQ+ y/o personas que se reconocen con orientaciones sexuales e identidades de género diversas?
            </Label>
            <Select
              value={formData.assoc_is_lgbtiq}
              onValueChange={(value) => setFormData({ ...formData, assoc_is_lgbtiq: value })}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="si">Sí</SelectItem>
                <SelectItem value="prefiero_no_decir">Prefiero no decirlo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>¿Considera usted que realiza trabajos de cuidado directo o indirecto, remunerados o no remunerado?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.assoc_is_caregiver === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_caregiver === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_caregiver: false })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.assoc_is_caregiver === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_caregiver === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_caregiver: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>¿Es usted un Veterano de la Fuerza Pública o un beneficiario de Veterano de la Fuerza Pública?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={formData.assoc_is_veteran === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_veteran === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_veteran: false })}
              >
                No
              </Button>
              <Button
                type="button"
                variant={formData.assoc_is_veteran === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_veteran === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, assoc_is_veteran: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          {/* País, Departamento, Municipio */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assoc_residence_country" className={labelClass}>
                País de Residencia *
              </Label>
              <Select
                value={formData.assoc_residence_country}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  assoc_residence_country: value,
                  assoc_residence_department: value === 'Colombia' ? formData.assoc_residence_department : '',
                  assoc_residence_municipality: value === 'Colombia' ? formData.assoc_residence_municipality : ''
                })}
                required
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Seleccione país..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assoc_nationality" className={labelClass}>
                Nacionalidad *
              </Label>
              <Select
                value={formData.assoc_nationality}
                onValueChange={(value) => setFormData({ ...formData, assoc_nationality: value })}
                required
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Seleccione nacionalidad..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.assoc_residence_country === 'Colombia' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assoc_residence_department" className={labelClass}>
                  Departamento *
                </Label>
                <Select
                  value={formData.assoc_residence_department}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    assoc_residence_department: value,
                    assoc_residence_municipality: ''
                  })}
                  required
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Seleccione departamento..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assoc_residence_municipality" className={labelClass}>
                  Municipio *
                </Label>
                <Select
                  value={formData.assoc_residence_municipality}
                  onValueChange={(value) => setFormData({ ...formData, assoc_residence_municipality: value })}
                  disabled={!formData.assoc_residence_department}
                  required
                >
                  <SelectTrigger className={selectTriggerClass} data-testid="assoc-residence-municipality-select">
                    <SelectValue placeholder={formData.assoc_residence_department ? "Seleccione municipio..." : "Primero seleccione departamento"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {formData.assoc_residence_department && colombiaData[formData.assoc_residence_department]?.map((muni) => (
                      <SelectItem key={muni} value={muni}>
                        {muni}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Rangos de edad automáticos */}
          <div className="space-y-2">
            <Label className={labelClass}>Persona entre 14 y 28 años</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                disabled
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_youth_14_28 ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-100'}`}
              >
                {formData.assoc_is_youth_14_28 ? 'Sí (calculado automáticamente)' : 'No (calculado automáticamente)'}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Persona de 60 años o más</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                disabled
                className={`flex-1 h-11 rounded-sm ${formData.assoc_is_senior_60_plus ? 'bg-green-100 border-green-500 text-green-700' : 'bg-gray-100'}`}
              >
                {formData.assoc_is_senior_60_plus ? 'Sí (calculado automáticamente)' : 'No (calculado automáticamente)'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Información Empresarial */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Información Empresarial</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_economic_activity" className={labelClass}>
              Actividad Económica
            </Label>
            <Input
              id="company_economic_activity"
              type="text"
              placeholder="Comercio, Servicios, etc."
              value={formData.company_economic_activity}
              onChange={(e) => setFormData({ ...formData, company_economic_activity: e.target.value })}
              className={inputClass}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_size" className={labelClass}>
              Tamaño de la Empresa
            </Label>
            <Select
              value={formData.company_size}
              onValueChange={(value) => setFormData({ ...formData, company_size: value })}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="micro">Microempresa</SelectItem>
                <SelectItem value="pequena">Pequeña</SelectItem>
                <SelectItem value="mediana">Mediana</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_entrepreneurship_stage" className={labelClass}>
              Etapa del Proceso Emprendedor
            </Label>
            <Select
              value={formData.company_entrepreneurship_stage}
              onValueChange={(value) => setFormData({ ...formData, company_entrepreneurship_stage: value })}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="idea">Idea</SelectItem>
                <SelectItem value="validacion">Validación</SelectItem>
                <SelectItem value="temprana">Temprana</SelectItem>
                <SelectItem value="crecimiento">Crecimiento</SelectItem>
                <SelectItem value="expansion">Expansión</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_education_level" className={labelClass}>
              Nivel Educativo
            </Label>
            <Select
              value={formData.company_education_level}
              onValueChange={(value) => setFormData({ ...formData, company_education_level: value })}
            >
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primaria">Primaria</SelectItem>
                <SelectItem value="bachillerato">Bachillerato</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
                <SelectItem value="tecnologo">Tecnólogo</SelectItem>
                <SelectItem value="profesional">Profesional</SelectItem>
                <SelectItem value="posgrado">Posgrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Características especiales */}
        <div className="space-y-2">
          <Label className={labelClass}>¿Tiene alguna condición de discapacidad?</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              variant={formData.company_has_disability === false ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_has_disability === false ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_has_disability: false, company_disability_type: '' })}
            >
              No
            </Button>
            <Button
              type="button"
              variant={formData.company_has_disability === true ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_has_disability === true ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_has_disability: true })}
            >
              Sí
            </Button>
          </div>
          {formData.company_has_disability && (
            <div className="space-y-2 pt-2">
              <Label htmlFor="company_disability_type" className={labelClass}>
                Tipo de discapacidad *
              </Label>
              <Select
                value={formData.company_disability_type}
                onValueChange={(value) => setFormData({ ...formData, company_disability_type: value })}
              >
                <SelectTrigger className={selectTriggerClass} data-testid="register-juridica-disability-type-select">
                  <SelectValue placeholder="Seleccione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auditiva">Auditiva</SelectItem>
                  <SelectItem value="visual">Visual</SelectItem>
                  <SelectItem value="fisica">Física</SelectItem>
                  <SelectItem value="sordoceguera">Sordoceguera</SelectItem>
                  <SelectItem value="intelectual">Intelectual</SelectItem>
                  <SelectItem value="psicosocial">Psicosocial</SelectItem>
                  <SelectItem value="multiple">Múltiple</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                  <SelectItem value="ninguna">Ninguna</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Grupo Étnico */}
        <div className="space-y-2">
          <Label htmlFor="company_ethnic_group" className={labelClass}>
            Grupo Étnico
          </Label>
          <Select
            value={formData.company_ethnic_group}
            onValueChange={(value) => setFormData({ ...formData, company_ethnic_group: value })}
          >
            <SelectTrigger className={selectTriggerClass} data-testid="company-ethnic-group-select">
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="negro">Negro</SelectItem>
              <SelectItem value="afro">Afro</SelectItem>
              <SelectItem value="raizal">Raizal</SelectItem>
              <SelectItem value="palenquero">Palenquero</SelectItem>
              <SelectItem value="indigena">Indígena</SelectItem>
              <SelectItem value="rrom_gitano">Rrom o Gitano</SelectItem>
              <SelectItem value="ninguno">Ninguno</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Ubicación Geográfica */}
        <div className="space-y-2">
          <Label htmlFor="company_geographic_location" className={labelClass}>
            Ubicación Geográfica
          </Label>
          <Select
            value={formData.company_geographic_location}
            onValueChange={(value) => setFormData({ ...formData, company_geographic_location: value })}
          >
            <SelectTrigger className={selectTriggerClass} data-testid="company-geographic-location-select">
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rural">Rural</SelectItem>
              <SelectItem value="urbano">Urbano</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Población Vulnerable */}
        <div className="space-y-2">
          <Label className={labelClass}>¿Eres población vulnerable?</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              data-testid="company-vulnerable-population-no"
              variant={formData.company_vulnerable_population === false ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_vulnerable_population === false ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_vulnerable_population: false, company_vulnerable_population_type: '' })}
            >
              No
            </Button>
            <Button
              type="button"
              data-testid="company-vulnerable-population-yes"
              variant={formData.company_vulnerable_population === true ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_vulnerable_population === true ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_vulnerable_population: true })}
            >
              Sí
            </Button>
          </div>
          {formData.company_vulnerable_population === true && (
            <div className="pt-2">
              <Label className={labelClass}>Tipo de población vulnerable *</Label>
              <Select
                value={formData.company_vulnerable_population_type}
                onValueChange={(value) => setFormData({ ...formData, company_vulnerable_population_type: value })}
              >
                <SelectTrigger className={selectTriggerClass} data-testid="company-vulnerable-type-select">
                  <SelectValue placeholder="Seleccione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Victima por desplazamiento forzado">Víctima por desplazamiento forzado</SelectItem>
                  <SelectItem value="En proceso de reincorporacion">En proceso de reincorporación</SelectItem>
                  <SelectItem value="Retornada">Retornada</SelectItem>
                  <SelectItem value="Residente en comunidades de acogida">Residente en comunidades de acogida</SelectItem>
                  <SelectItem value="En proceso de reintegracion">En proceso de reintegración</SelectItem>
                  <SelectItem value="Ninguna">Ninguna</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Víctima con RUV */}
        <div className="space-y-2">
          <Label className={labelClass}>¿Eres víctima con RUV?</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              data-testid="company-victim-ruv-no"
              variant={formData.company_victim_ruv === false ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_victim_ruv === false ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_victim_ruv: false, company_victim_ruv_number: '' })}
            >
              No
            </Button>
            <Button
              type="button"
              data-testid="company-victim-ruv-yes"
              variant={formData.company_victim_ruv === true ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_victim_ruv === true ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_victim_ruv: true })}
            >
              Sí
            </Button>
          </div>
          {formData.company_victim_ruv === true && (
            <div className="pt-2">
              <Label htmlFor="company_victim_ruv_number" className={labelClass}>Número RUV *</Label>
              <Input
                id="company_victim_ruv_number"
                type="text"
                value={formData.company_victim_ruv_number}
                onChange={(e) => setFormData({ ...formData, company_victim_ruv_number: e.target.value })}
                placeholder="Ingrese número RUV"
                className={inputClass}
                data-testid="company-victim-ruv-number-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Información del Beneficiario */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Información del Beneficiario</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_beneficiary_neighborhood" className={labelClass}>Barrio</Label>
            <Input
              id="company_beneficiary_neighborhood"
              type="text"
              value={formData.company_beneficiary_neighborhood}
              onChange={(e) => setFormData({ ...formData, company_beneficiary_neighborhood: e.target.value })}
              placeholder="Nombre del barrio"
              className={inputClass}
              data-testid="company-beneficiary-neighborhood-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_beneficiary_address" className={labelClass}>Dirección</Label>
            <Input
              id="company_beneficiary_address"
              type="text"
              value={formData.company_beneficiary_address}
              onChange={(e) => setFormData({ ...formData, company_beneficiary_address: e.target.value })}
              placeholder="Cra 10 # 20-30"
              className={inputClass}
              data-testid="company-beneficiary-address-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_beneficiary_phone" className={labelClass}>Teléfono (10 dígitos)</Label>
            <Input
              id="company_beneficiary_phone"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={formData.company_beneficiary_phone}
              onChange={(e) => setFormData({ ...formData, company_beneficiary_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              placeholder="3001234567"
              className={inputClass}
              data-testid="company-beneficiary-phone-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_beneficiary_other_phone" className={labelClass}>Otro Teléfono (10 dígitos)</Label>
            <Input
              id="company_beneficiary_other_phone"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={formData.company_beneficiary_other_phone}
              onChange={(e) => setFormData({ ...formData, company_beneficiary_other_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              placeholder="3007654321"
              className={inputClass}
              data-testid="company-beneficiary-other-phone-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_beneficiary_email" className={labelClass}>Correo Electrónico del Beneficiario</Label>
          <Input
            id="company_beneficiary_email"
            type="email"
            value={formData.company_beneficiary_email}
            onChange={(e) => setFormData({ ...formData, company_beneficiary_email: e.target.value })}
            placeholder="beneficiario@correo.com"
            className={inputClass}
            data-testid="company-beneficiary-email-input"
          />
        </div>
      </div>

      {/* Producto y Sector */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Producto y Sector Económico</h3>

        <div className="space-y-2">
          <Label htmlFor="company_main_product" className={labelClass}>Producto Principal (Bien/Servicio)</Label>
          <Input
            id="company_main_product"
            type="text"
            value={formData.company_main_product}
            onChange={(e) => setFormData({ ...formData, company_main_product: e.target.value })}
            placeholder="Ej: Servicios de consultoría tecnológica"
            className={inputClass}
            data-testid="company-main-product-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_economic_sector" className={labelClass}>Sector Económico</Label>
            <Select
              value={formData.company_economic_sector}
              onValueChange={(value) => setFormData({ ...formData, company_economic_sector: value, company_economic_subsector: '' })}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="company-economic-sector-select">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {SECTORES_LIST.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_economic_subsector" className={labelClass}>Subsector Económico</Label>
            <Select
              value={formData.company_economic_subsector}
              onValueChange={(value) => setFormData({ ...formData, company_economic_subsector: value })}
              disabled={!formData.company_economic_sector}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="company-economic-subsector-select">
                <SelectValue placeholder={formData.company_economic_sector ? "Seleccione..." : "Seleccione un sector primero"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {(SECTORES_ECONOMICOS[formData.company_economic_sector] || []).map((sub) => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Formalización */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Formalización</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>¿Tiene RUT?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                data-testid="company-has-rut-no"
                variant={formData.company_has_rut === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.company_has_rut === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, company_has_rut: false })}
              >
                No
              </Button>
              <Button
                type="button"
                data-testid="company-has-rut-yes"
                variant={formData.company_has_rut === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.company_has_rut === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, company_has_rut: true })}
              >
                Sí
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>¿Tiene Registro Mercantil?</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                data-testid="company-has-mercantile-no"
                variant={formData.company_has_mercantile_registry === false ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.company_has_mercantile_registry === false ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, company_has_mercantile_registry: false, company_mercantile_registry_date: '', company_formalization_years: null })}
              >
                No
              </Button>
              <Button
                type="button"
                data-testid="company-has-mercantile-yes"
                variant={formData.company_has_mercantile_registry === true ? "default" : "outline"}
                className={`flex-1 h-11 rounded-sm ${formData.company_has_mercantile_registry === true ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => setFormData({ ...formData, company_has_mercantile_registry: true })}
              >
                Sí
              </Button>
            </div>
          </div>
        </div>

        {formData.company_has_mercantile_registry === true && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_mercantile_registry_date" className={labelClass}>Fecha de Matrícula Mercantil</Label>
              <Input
                id="company_mercantile_registry_date"
                type="date"
                value={formData.company_mercantile_registry_date}
                onChange={(e) => handleMercantileRegistryDateChange(e.target.value)}
                className={inputClass}
                data-testid="company-mercantile-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Años de Formalización</Label>
              <Input
                value={formData.company_formalization_years !== null && formData.company_formalization_years !== undefined ? `${formData.company_formalization_years} años` : ''}
                placeholder="Calculado automáticamente"
                readOnly
                className={`${inputClass} bg-gray-100 cursor-not-allowed`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Datos Empresariales */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Datos Empresariales</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_employees_count" className={labelClass}>Número de Empleados</Label>
            <Select
              value={formData.company_employees_count ? String(formData.company_employees_count) : ''}
              onValueChange={(value) => setFormData({ ...formData, company_employees_count: parseInt(value, 10) })}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="company-employees-count-select">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {Array.from({ length: 100 }, (_, i) => i + 1).map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_size_category" className={labelClass}>Tamaño de Empresa</Label>
            <Select
              value={formData.company_size_category}
              onValueChange={(value) => setFormData({ ...formData, company_size_category: value })}
            >
              <SelectTrigger className={selectTriggerClass} data-testid="company-size-category-select">
                <SelectValue placeholder="Seleccione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Microempresa">Microempresa</SelectItem>
                <SelectItem value="Pequena">Pequeña</SelectItem>
                <SelectItem value="Mediana">Mediana</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_monthly_sales_intervention" className={labelClass}>Ventas Mensuales (Inicio Intervención)</Label>
            <Input
              id="company_monthly_sales_intervention"
              type="text"
              inputMode="numeric"
              value={formData.company_monthly_sales_intervention}
              onChange={(e) => setFormData({ ...formData, company_monthly_sales_intervention: e.target.value.replace(/[^\d]/g, '') })}
              placeholder="Ej: 5000000"
              className={inputClass}
              data-testid="company-monthly-sales-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_total_assets_last_period" className={labelClass}>Valor Total de Activos (Último Período)</Label>
            <Input
              id="company_total_assets_last_period"
              type="text"
              inputMode="numeric"
              value={formData.company_total_assets_last_period}
              onChange={(e) => setFormData({ ...formData, company_total_assets_last_period: e.target.value.replace(/[^\d]/g, '') })}
              placeholder="Ej: 50000000"
              className={inputClass}
              data-testid="company-total-assets-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className={labelClass}>¿Empresa sin ánimo de lucro?</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              data-testid="company-non-profit-no"
              variant={formData.company_non_profit === false ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_non_profit === false ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_non_profit: false })}
            >
              No
            </Button>
            <Button
              type="button"
              data-testid="company-non-profit-yes"
              variant={formData.company_non_profit === true ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_non_profit === true ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_non_profit: true })}
            >
              Sí
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label className={labelClass}>¿Tiene página web?</Label>
          <div className="flex gap-3">
            <Button
              type="button"
              data-testid="company-has-website-no"
              variant={formData.company_has_website === false ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_has_website === false ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_has_website: false, company_website: '' })}
            >
              No
            </Button>
            <Button
              type="button"
              data-testid="company-has-website-yes"
              variant={formData.company_has_website === true ? "default" : "outline"}
              className={`flex-1 h-11 rounded-sm ${formData.company_has_website === true ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setFormData({ ...formData, company_has_website: true })}
            >
              Sí
            </Button>
          </div>
          {formData.company_has_website && (
            <div className="space-y-2 pt-2">
              <Label htmlFor="company_website" className={labelClass}>URL de la página web</Label>
              <Input
                id="company_website"
                type="url"
                value={formData.company_website}
                onChange={(e) => setFormData({ ...formData, company_website: e.target.value })}
                placeholder="https://www.miempresa.com"
                className={inputClass}
                data-testid="company-website-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Áreas de Interés */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Me Interesa</h3>
        <p className="text-xs text-muted-foreground">Seleccione las áreas en las que está interesado (puede elegir varias)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            'Asesorias especializadas',
            'Contactos',
            'Desarrollar nuevos productos o servicios',
            'Financiacion',
            'Informacion general de iNNpulsa',
            'Participar en eventos',
            'Procesos de internacionalizacion de productos',
            'Socios inversionistas'
          ].map((option) => {
            const checked = formData.company_interests.includes(option);
            return (
              <label
                key={option}
                className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-all ${
                  checked
                    ? 'border-primary bg-primary/10'
                    : darkMode ? 'border-gray-700 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                data-testid={`company-interest-${option.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleInterest(option)}
                />
                <span className="text-sm">{option}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Correo y Contraseña */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-primary">Datos de Acceso</h3>
        
        <div className="space-y-2">
          <Label htmlFor="email" className={labelClass}>
            Correo Electrónico Corporativo *
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="info@empresa.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="pl-10 h-11 rounded-sm"
              required
              data-testid="register-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className={labelClass}>
            Contraseña *
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pl-10 h-11 rounded-sm"
              required
              minLength={6}
              data-testid="register-password-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
        </div>
      </div>

      {/* Autorización */}
      <div className="flex items-start space-x-2 p-4 bg-secondary/50 rounded-sm border border-border">
        <Checkbox
          id="data_authorization"
          checked={formData.data_authorization}
          onCheckedChange={(checked) => setFormData({ ...formData, data_authorization: checked })}
          required
        />
        <Label htmlFor="data_authorization" className="text-sm cursor-pointer leading-relaxed">
          <strong>AUTORIZACIÓN PARA EL TRATAMIENTO DE DATOS PERSONALES</strong><br /><br />
          Autorizo de manera previa, expresa, informada e inequívoca a <strong>iNNpulsa Colombia</strong>, para que, de conformidad con la Ley 1581 de 2012 y demás normas aplicables en materia de protección de datos personales, realice el tratamiento de mis datos personales mediante su recolección, almacenamiento, uso, circulación, supresión, transmisión y transferencia, de acuerdo con las siguientes finalidades: enviarme información sobre sus instrumentos, invitaciones, capacitaciones, programas, eventos y demás actividades que puedan fortalecer y potencializar mi quehacer y contribuir a la superación de mis desafíos; las demás finalidades incluidas en la política de protección de datos; así como compartir mi información con aliados, operadores y proveedores, cuando ello sea necesario para el desarrollo de las actividades y demás finalidades previamente informadas y autorizadas.<br /><br />
          Como titular de los datos personales, tengo derecho a conocer, acceder, actualizar y rectificar mi información, presentar consultas y reclamos; acceder gratuitamente a mis datos personales y, cuando sea procedente, solicitar su supresión o revocar la autorización.<br /><br />
          <em>*Para conocer la Política de Tratamiento de Datos Personales de iNNpulsa Colombia, así como las finalidades del tratamiento, los procedimientos y canales habilitados para presentar consultas, solicitudes o reclamos, y los derechos que me asisten como titular de los datos, puede consultarla en:{' '}
          <a href="https://www.innpulsacolombia.com/transparencia/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            https://www.innpulsacolombia.com/transparencia/
          </a>.</em> *
        </Label>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-11 text-white font-medium transition-all active:scale-95 rounded-xl"
        style={{ background: 'linear-gradient(135deg, #9B1427 0%, #C41E3A 100%)' }}
        data-testid="register-submit-button"
      >
        {loading ? 'Creando cuenta...' : 'Crear cuenta'}
      </Button>
      </form>
    </div>
  );
}
